import { useMemo, useCallback } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridCellParams, type GridRenderEditCellParams } from '@mui/x-data-grid';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, UserCheck, Briefcase, MessageSquare, TrendingUp, Landmark, Link, Paperclip, Target } from 'lucide-react';
import { useNodeWithRelationships, useUpdateEdge } from '@lingaai/shared-hooks';
import { formatDate, formatDateTime, formatNumber } from '@lingaai/shared-utils';
import { NODE_REGISTRY, type RelationshipSectionConfig } from '@lingaai/types/graph/nodes';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

dayjs.extend(utc);

/** Icon mapping for section icons */
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
  Building2,
  Users,
  UserCheck,
  Briefcase,
  MessageSquare,
  TrendingUp,
  Landmark,
  Link,
  Paperclip,
  Target,
};

interface RelationshipsSectionProps {
  nodeId: string;
  nodeType: string;
}

interface OutgoingEdge {
  id: string;
  type: string;
  props: Record<string, unknown>;
  dst_node: {
    id: string;
    type: string;
    props: Record<string, unknown>;
  };
}

interface IncomingEdge {
  id: string;
  type: string;
  props: Record<string, unknown>;
  src_node: {
    id: string;
    type: string;
    props: Record<string, unknown>;
  };
}

interface ProcessedRelationship {
  id: string;
  edgeId: string;
  edgeType: string;
  edgeProps: Record<string, unknown>;
  nodeId: string;
  nodeType: string;
  nodeProps: Record<string, unknown>;
  name: string;
}

/**
 * Process edges into a flat list of relationships for a section
 */
function processEdgesForSection(config: RelationshipSectionConfig, outgoingEdges: OutgoingEdge[], incomingEdges: IncomingEdge[]): ProcessedRelationship[] {
  const relationships: ProcessedRelationship[] = [];
  if (config.direction === 'outgoing' || config.direction === 'both') {
    outgoingEdges
      .filter((edge) => config.edgeTypes.includes(edge.type))
      .filter((edge) => !config.relatedNodeTypes || config.relatedNodeTypes.includes(edge.dst_node.type))
      .forEach((edge) => {
        relationships.push({
          id: edge.id,
          edgeId: edge.id,
          edgeType: edge.type,
          edgeProps: edge.props,
          nodeId: edge.dst_node.id,
          nodeType: edge.dst_node.type,
          nodeProps: edge.dst_node.props,
          name: (edge.dst_node.props.name as string) || (edge.dst_node.props.subject as string) || 'Unnamed',
        });
      });
  }
  if (config.direction === 'incoming' || config.direction === 'both') {
    incomingEdges
      .filter((edge) => config.edgeTypes.includes(edge.type))
      .filter((edge) => !config.relatedNodeTypes || config.relatedNodeTypes.includes(edge.src_node.type))
      .forEach((edge) => {
        relationships.push({
          id: edge.id,
          edgeId: edge.id,
          edgeType: edge.type,
          edgeProps: edge.props,
          nodeId: edge.src_node.id,
          nodeType: edge.src_node.type,
          nodeProps: edge.src_node.props,
          name: (edge.src_node.props.name as string) || (edge.src_node.props.subject as string) || 'Unnamed',
        });
      });
  }
  return relationships;
}

/**
 * Grid renderer for relationship sections with inline editing
 */
function RelationshipGrid({ relationships, config, onNavigate }: { relationships: ProcessedRelationship[]; config: RelationshipSectionConfig; onNavigate: (type: string, id: string) => void }) {
  const updateEdgeMutation = useUpdateEdge();

  const handleCellClick = useCallback(
    (params: GridCellParams<ProcessedRelationship>) => {
      // Only navigate on first column (node link) click
      if (params.field === config.columns?.[0]?.field) {
        onNavigate(params.row.nodeType, params.row.nodeId);
      }
    },
    [config.columns, onNavigate]
  );

  const handleProcessRowUpdate = useCallback(
    async (newRow: ProcessedRelationship, oldRow: ProcessedRelationship) => {
      // Find which field changed
      const changedFields = Object.keys(newRow).filter((key) => newRow[key as keyof ProcessedRelationship] !== oldRow[key as keyof ProcessedRelationship]);

      if (changedFields.length === 0) return oldRow;

      // Update edge props
      const updatedProps = { ...oldRow.edgeProps };
      changedFields.forEach((field) => {
        updatedProps[field] = newRow[field as keyof ProcessedRelationship];
      });

      await updateEdgeMutation.mutateAsync({
        edgeId: oldRow.edgeId,
        updates: { props: updatedProps },
      });

      return newRow;
    },
    [updateEdgeMutation]
  );

  const columns: GridColDef[] = useMemo(() => {
    if (!config.columns) return [];
    return config.columns.map((col, index) => {
      const isFirstColumn = index === 0;

      return {
        ...col,
        type: undefined,
        editable: col.editable ?? false,
        valueGetter: (_value: unknown, row: ProcessedRelationship) => {
          if (col.field === 'name') return row.name;
          if (col.field === 'type') return row.nodeType;
          // Get from correct source - return raw value
          const rawValue = col.source === 'edge' ? row.edgeProps[col.field] : row.nodeProps[col.field];
          return rawValue ?? '';
        },
        valueFormatter: (value: unknown) => {
          if (!value) return '';
          // Format dates for display only
          if (col.type === 'datetime' && typeof value === 'string') {
            return formatDateTime(value);
          }
          if (col.type === 'date' && typeof value === 'string') {
            return formatDate(value);
          }
          // Format numbers with currency/percentage/year if specified
          if (col.type === 'number' && typeof value === 'number' && (col as any).format) {
            return formatNumber(value, (col as any).format);
          }
          return String(value);
        },
        renderCell: isFirstColumn
          ? (params: GridRenderCellParams<ProcessedRelationship>) => (
              <Box
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {params.value}
              </Box>
            )
          : undefined,
        renderEditCell:
          col.type === 'date' || col.type === 'datetime'
            ? (params: GridRenderEditCellParams<ProcessedRelationship>) => {
                const Picker = col.type === 'datetime' ? DateTimePicker : DatePicker;
                return (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Picker
                      value={params.value ? (col.type === 'date' ? dayjs.utc(params.value as string) : dayjs(params.value as string)) : null}
                      onChange={(newValue) => {
                        const formattedValue = col.type === 'datetime' ? newValue?.toISOString() || '' : newValue?.format('YYYY-MM-DD') || '';
                        params.api.setEditCellValue({
                          id: params.id,
                          field: params.field,
                          value: formattedValue,
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          variant: 'standard',
                        },
                        openPickerIcon: {
                          sx: { fontSize: '1rem' },
                        },
                      }}
                    />
                  </LocalizationProvider>
                );
              }
            : undefined,
      };
    });
  }, [config.columns]);
  return (
    <DataGrid
      rows={relationships}
      columns={columns}
      density="compact"
      autoHeight
      pageSizeOptions={[5, 10, 25]}
      initialState={{
        pagination: { paginationModel: { pageSize: 5 } },
      }}
      onCellClick={handleCellClick}
      processRowUpdate={handleProcessRowUpdate}
      disableRowSelectionOnClick
      sx={{
        border: 'none',
        '& .MuiDataGrid-cell:focus': { outline: 'none' },
        '& .MuiDataGrid-cell--editable': {
          cursor: 'text',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        },
      }}
    />
  );
}

/**
 * Single relationship section
 */
function RelationshipSection({ config, relationships, onNavigate }: { config: RelationshipSectionConfig; relationships: ProcessedRelationship[]; onNavigate: (type: string, id: string) => void }) {
  const IconComponent = ICON_MAP[config.icon];
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {IconComponent && <IconComponent size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />}
        <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1 }}>
          {config.sectionLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
          ({relationships.length})
        </Typography>
      </Box>
      <RelationshipGrid relationships={relationships} config={config} onNavigate={onNavigate} />
    </Box>
  );
}

export function RelationshipsSection({ nodeId, nodeType }: RelationshipsSectionProps) {
  const navigate = useNavigate();
  const { data: relationships, isLoading } = useNodeWithRelationships(nodeId);

  const sectionConfigs = useMemo(() => NODE_REGISTRY[nodeType]?.relationshipSections ?? [], [nodeType]);

  // Transform the relationships data to match our expected format
  const outgoingEdges: OutgoingEdge[] = useMemo(() => {
    if (!relationships?.outgoing) return [];
    return relationships.outgoing
      .filter((rel) => rel.targetNode)
      .map((rel) => ({
        id: rel.edge.id,
        type: rel.edge.type,
        props: rel.edge.props,
        dst_node: {
          id: rel.targetNode.id,
          type: rel.targetNode.type,
          props: rel.targetNode.props,
        },
      }));
  }, [relationships]);

  const incomingEdges: IncomingEdge[] = useMemo(() => {
    if (!relationships?.incoming) return [];
    return relationships.incoming
      .filter((rel) => rel.sourceNode)
      .map((rel) => ({
        id: rel.edge.id,
        type: rel.edge.type,
        props: rel.edge.props,
        src_node: {
          id: rel.sourceNode.id,
          type: rel.sourceNode.type,
          props: rel.sourceNode.props,
        },
      }));
  }, [relationships]);

  const sectionsWithData = useMemo(() => {
    return sectionConfigs
      .map((config) => ({
        config,
        relationships: processEdgesForSection(config, outgoingEdges, incomingEdges),
      }))
      .filter((section) => section.relationships.length > 0);
  }, [sectionConfigs, outgoingEdges, incomingEdges]);

  const handleNavigate = (type: string, id: string) => {
    // Routes use plural form (e.g., /deals/, /people/, /opportunities/)
    const routeMap: Record<string, string> = {
      // Irregular plurals
      Person: 'people',
      Opportunity: 'opportunities',
      Activity: 'activities',
      // Hyphenated routes
      FundraisingOpportunity: 'fundraising',
      ExternalFund: 'external-funds',
      CapitalCall: 'capital-calls',
      InvestmentAccount: 'investment-accounts',
      PortfolioCompany: 'portfolio-companies',
      // Contacts alias
      Contact: 'contacts',
    };
    const route = routeMap[type] || `${type.toLowerCase()}s`;
    navigate(`/${route}/${id}`);
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  if (sectionsWithData.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">No relationships</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {sectionsWithData.map(({ config, relationships }) => (
        <RelationshipSection key={config.sectionLabel} config={config} relationships={relationships} onNavigate={handleNavigate} />
      ))}
    </Paper>
  );
}
